import React, { useEffect, useState } from "react";
import {
  fetchCoinsForSelect,
  fetchIntraDayCoinsForSelect,
} from "../../../../api/coin";
import { Modal, Nav, Tab, Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Trade from "./trade";
import { usePrices } from "../../../../context/SocketContext";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { deleteTransaction } from "../../../../api/transaction";

const UserTrade = () => {
  const [spotCoins, setSpotCoins] = useState([]);
  const [futuresCoins, setFuturesCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 574);
  const [activeTab, setActiveTab] = useState("Spot");

  // Live prices from socket
  const prices = usePrices();

  // Build a lookup map: { coinId: currentPrice }
  const coinPrices = React.useMemo(() => {
    const map = {};
    prices.forEach(({ coinId, price }) => {
      map[coinId] = Number(price);
    });
    return map;
  }, [prices]);

  // Fetch user coins for both Spot & Futures
  const fetchCoins = async () => {
    setLoading(true);
    setError(null);

    try {
      if (activeTab === "Spot") {
        const spotData = await fetchCoinsForSelect();
        setSpotCoins(spotData);
      } else if (activeTab === "Futures") {
        const futuresData = await fetchIntraDayCoinsForSelect();
        setFuturesCoins(futuresData);
      }
    } catch (err) {
      console.error("Failed to load coins", err);
      setError("Failed to load coins");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCoins();
  }, [activeTab, showModal]);

  const handleManageTrade = (coin) => {
    setSelectedCoin({ ...coin });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedCoin(null);
  };

  const handleTradeSuccess = () => {
    handleClose();
  };
  // Example delete handler (replace with API call)
  const handleDeleteTrade = async (order) => {
    try {
      await deleteTransaction(order.id);
      Swal.fire({
        icon: "success",
        title: `Deleted successful!`,
        timer: 1500,
        showConfirmButton: false,
      });
      // TODO: Call delete API here, e.g. await deleteOrder(order.id);
      fetchCoins();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: `Failed to delete order.`,
        timer: 1500,
        showConfirmButton: false,
      });
      console.error("Delete failed:", err);
    }
  };

  // --- Spot table renderer ---
  const renderSpotTable = () => {
    // Group only MARKET trades
    const groupedMarketTrades = spotCoins.reduce((acc, trade) => {
      if (trade.orderType !== "market") return acc; // ✅ only handle market orders

      const { transactionType, price, quantity, coin } = trade;
      const key = `${coin.id}_market`;

      if (!acc[key]) {
        acc[key] = {
          coin,
          orderType: "market",
          buyQty: 0,
          buyValue: 0,
          sellQty: 0,
        };
      }

      if (transactionType === "buy") {
        acc[key].buyQty += Number(quantity);
        acc[key].buyValue += Number(price) * Number(quantity);
      } else if (transactionType === "sell") {
        acc[key].sellQty += Number(quantity);
      }

      return acc;
    }, {});

    const mergedMarketData = Object.values(groupedMarketTrades)
      .map((entry) => {
        const avgBuyPrice =
          entry.buyQty > 0 ? entry.buyValue / entry.buyQty : 0;
        const remainingQty = entry.buyQty - entry.sellQty;

        // Round to 10 decimals for crypto precision
        const normalizedQty = Number(remainingQty.toFixed(10));

        return {
          coin: entry.coin,
          orderType: entry.orderType,
          quantity: normalizedQty,
          price: avgBuyPrice,
          transactionType: "buy", // ✅ always "buy" for remaining positions
        };
      })
      .filter((entry) => entry.quantity > 0.000000000000001); // ✅ Only show if remaining qty > 0

    // ✅ Keep market merged (only positive quantities), and add back ALL non-market trades
    const finalData = [
      ...mergedMarketData,
      ...spotCoins.filter((t) => t.orderType !== "market"), // All limit orders as-is
    ];

    return (
      <div className="table-responsive dataTablehistory">
        <Table className="table shadow-hover display">
          <thead>
            <tr>
              <th>Coin</th>
              <th>Name</th>
              <th>Symbol</th>
              <th>Transaction</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Price</th>
              <th>Current Price</th>
              <th>P/L %</th>
              <th>P/L</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="text-center p-3">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={11} className="text-center text-danger p-3">
                  {error}
                </td>
              </tr>
            ) : finalData.length > 0 ? (
              finalData.map((spotCoin, idx) => {
                const currentPrice = coinPrices[spotCoin.coin.id] || 0;
                const profitLossPercent =
                  spotCoin.price > 0
                    ? ((currentPrice - spotCoin.price) / spotCoin.price) * 100
                    : 0;
                const profitLossAmount =
                  (Number(currentPrice) - Number(spotCoin.price)) *
                  Number(spotCoin.quantity);
                const color = profitLossPercent >= 0 ? "green" : "red";

                return (
                  <tr key={`spot-${spotCoin.coin.id}-${idx}`}>
                    <td style={{ textAlign: "center" }}>
                      <Link to={`/coin-details/${spotCoin.coin.id}`}>
                        <img
                          src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${spotCoin.coin.logo}`}
                          alt={spotCoin.coin.symbol}
                          style={{ width: 32, height: 32, borderRadius: "50%" }}
                        />
                      </Link>
                    </td>
                    <td>{spotCoin.coin.name}</td>
                    <td>{spotCoin.coin.symbol}</td>
                    <td>{spotCoin.transactionType || "-"}</td>
                    <td>{spotCoin.orderType}</td>
                    <td>
                      {spotCoin.quantity > 0
                        ? Number(spotCoin.quantity).toFixed(8)
                        : "-"}
                    </td>
                    <td>${Number(spotCoin.price).toFixed(2)}</td>
                    <td>${Number(currentPrice).toFixed(4)}</td>
                    <td style={{ color }}>{profitLossPercent.toFixed(2)}%</td>
                    <td style={{ color }}>${profitLossAmount.toFixed(2)}</td>
                    <td className="d-flex flex-column gap-2">
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handleManageTrade(spotCoin)}
                      >
                        Manage Spot
                      </button>

                      {/* ✅ Delete button only for limit orders */}
                      {spotCoin.orderType?.toLowerCase() === "limit" && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteTrade(spotCoin)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} className="text-center">
                  No Spot trades found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  // --- Futures table renderer ---
  const renderFuturesTable = () => (
    <div className="table-responsive dataTablehistory">
      <Table className="table shadow-hover display">
        <thead>
          <tr>
            <th>Coin</th>
            <th>Name</th>
            <th>Symbol</th>
            <th>Order Type</th>
            <th>Transaction</th>
            <th>Quantity</th>
            <th>Entry Price</th>
            <th>Exit Price</th>
            <th>Current Price</th>
            <th>P/L %</th>
            <th>P/L($)</th>
            <th>Stop Loss</th>
            <th>Take Profit</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={13} className="text-center p-3">
                Loading...
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={13} className="text-center text-danger p-3">
                {error}
              </td>
            </tr>
          ) : futuresCoins.length > 0 ? (
            futuresCoins.map((trade) => {
              const currentPrice = coinPrices[trade.coin.id] || 0;
              const effectiveExitPrice = trade.exitPrice ?? currentPrice;

              const profitLossPercent =
                trade.entryPrice > 0
                  ? ((effectiveExitPrice - trade.entryPrice) /
                      trade.entryPrice) *
                    100
                  : 0;

              const realizedPnL =
                (effectiveExitPrice - trade.entryPrice) *
                Number(trade.quantity);

              const color = realizedPnL >= 0 ? "green" : "red";

              return (
                <tr key={`futures-${trade.id}`}>
                  <td style={{ textAlign: "center" }}>
                    <Link to={`/coin-details/${trade.coin.id}`}>
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${trade.coin.logo}`}
                        alt={trade.coin.symbol}
                        style={{ width: 32, height: 32, borderRadius: "50%" }}
                      />
                    </Link>
                  </td>
                  <td>{trade.coin.name}</td>
                  <td>{trade.coin.symbol}</td>
                  <td>{trade.orderType}</td>
                  <td
                    style={{
                      color: trade.transactionType === "buy" ? "green" : "red",
                    }}
                  >
                    {trade.transactionType}
                  </td>

                  <td>{Number(trade.quantity)}</td>
                  <td>${Number(trade.entryPrice).toFixed(2)}</td>
                  <td>{`$${Number(effectiveExitPrice).toFixed(4)}`}</td>
                  <td>${Number(currentPrice).toFixed(2)}</td>
                  <td style={{ color }}>{profitLossPercent.toFixed(4)}%</td>
                  <td style={{ color }}>${realizedPnL.toFixed(4)}</td>
                  <td>${trade.stopLoss}</td>
                  <td>${trade.takeProfit}</td>
                  <td>{trade.status}</td>
                  <td className="d-flex flex-column gap-2">
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleManageTrade(trade)}
                    >
                      Manage Spot
                    </button>

                    {/* ✅ Delete button only for limit orders */}
                    {trade.orderType?.toLowerCase() === "limit" && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteTrade(trade)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={13} className="text-center">
                No Futures trades found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );

  return (
    <Tab.Container defaultActiveKey="Spot" onSelect={(k) => setActiveTab(k)}>
      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-header border-0 d-flex justify-content-between align-items-center flex-wrap">
              <h4 className="heading mb-2">Trade Statement</h4>
              <Nav className="order nav nav-tabs">
                <Nav.Item>
                  <Nav.Link
                    eventKey="Spot"
                    className={isMobile ? "border rounded px-3 py-2" : ""}
                  >
                    Spot
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="Futures"
                    className={isMobile ? "border rounded px-3 py-2" : ""}
                  >
                    Futures
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          </div>
        </div>

        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              <Tab.Content>
                <Tab.Pane eventKey="Spot">{renderSpotTable()}</Tab.Pane>
                <Tab.Pane eventKey="Futures">{renderFuturesTable()}</Tab.Pane>
              </Tab.Content>
            </div>
          </div>
        </div>
      </div>

      {/* Trade Modal */}
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Manage Trade</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 5 }}>
          {selectedCoin ? (
            <Trade
              orderDetails={selectedCoin}
              coin={selectedCoin.coin}
              onTradeSuccess={handleTradeSuccess}
              from={activeTab}
            />
          ) : (
            <p>Loading...</p>
          )}
        </Modal.Body>
      </Modal>
    </Tab.Container>
  );
};

export default UserTrade;
